import { useFormik } from 'formik';
import useTranslate from 'hooks/useTranslate';
import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import ReactSelect from 'react-select';
import { useCountryListQuery, useIndustriesListQuery } from 'services';
import * as Yup from 'yup';
import ActionMenu from '../ActionMenu';
import CustomSearchAddress from 'components/Common/CustomSearchAddress';

interface PersonalInfoInterface {
	firstName: string;
	lastName: string;
	email: string;
	website: string;
	phoneNumber: string;
	designation: string;
	industryId: string;
	countryId: string;
	stateId: string;
	cityId: string;
	address: string;
	countryCode: string;
}

const PersonalInfo = ({ data, slug, onHandleChange, handleTitleChange }: any) => {
	// mutation
	const { data: countries = [] } = useCountryListQuery();
	const { data: { industries: industrieslist = [] } = {} } = useIndustriesListQuery();
	const { translate } = useTranslate();
	const firstNameRef = useRef<HTMLInputElement>(null);

	const industriesArr = useMemo(() => {
		const arr = [{ id: '', title: translate('PlaceHolder.selectIndustry') }];
		if (industrieslist?.length) {
			arr.push(...industrieslist);
		}
		return arr;
	}, [industrieslist]);

	useEffect(() => {
		if (firstNameRef.current) {
			firstNameRef.current.focus();
		}
	}, []);

	const validationSchema = Yup.object().shape({
		firstName: Yup.string().required(
			translate('Validation.required', { field: translate('PlaceHolder.firstName') }),
		),
		lastName: Yup.string().required(
			translate('Validation.required', { field: translate('PlaceHolder.lastName') }),
		),
		email: Yup.string()
			.email(translate('Validation.email'))
			.required(translate('Validation.required', { field: translate('PlaceHolder.email') })),
		website: Yup.string()
			.url(translate('Validation.url', { field: translate('PlaceHolder.website') }))
			.optional(),
		phoneNumber: Yup.string()
			.required(translate('Validation.required', { field: translate('PlaceHolder.phone') }))
			.matches(
				/^[0-9+]+$/,
				translate('Validation.match', { field: translate('PlaceHolder.phone') }),
			)
			.min(6, translate('Validation.min', { field: translate('PlaceHolder.phone'), number: '6' }))
			.max(
				15,
				translate('Validation.max', { field: translate('PlaceHolder.phone'), number: '15' }),
			),
		designation: Yup.string().required(
			translate('Validation.required', { field: translate('PlaceHolder.jobTitle') }),
		),
		industryId: Yup.string().required(
			translate('Validation.required', { field: translate('PlaceHolder.industry') }),
		),
		countryId: Yup.string().required(
			translate('Validation.required', { field: translate('PlaceHolder.country') }),
		),
		stateId: Yup.string().required(
			translate('Validation.required', { field: translate('PlaceHolder.state') }),
		),
		cityId: Yup.string().required(
			translate('Validation.required', { field: translate('PlaceHolder.city') }),
		),
	});
	const formik: any = useFormik({
		initialValues: {
			firstName: data?.firstName ?? '',
			lastName: data?.lastName ?? '',
			email: data?.email ?? '',
			website: data?.website ?? '',
			phoneNumber: data?.phoneNumber ?? '',
			designation: data?.designation ?? '',
			industryId: data?.industryId ?? '',
			countryId: data?.countryId ?? '',
			stateId: data?.stateId ?? '',
			cityId: data?.cityId ?? '',
			countryCode: data?.countryCode ?? '91',
			address: data?.address ?? '',
		},
		enableReinitialize: true,
		validationSchema: validationSchema,
		onSubmit: () => {},
	});

	const renderError = <T extends keyof PersonalInfoInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	const handleInputChange = (_value: any) => {
		formik.setFieldValue('address', '');
		formik.setFieldValue('cityId', '');
		formik.setFieldValue('stateId', '');
		formik.setFieldValue('countryId', '');
	};

	const handleChange = (slug: any, event: any) => {
		onHandleChange(slug, event);
		formik.setFieldValue(event.target.name, event.target.value);
	};

	return (
		<div className="resume-info">
			<Form onSubmit={formik.handleSubmit} autoComplete="off">
				<div className="build-resume-edit-header">
					<ActionMenu
						data={data}
						slug="detailTitle"
						contentSlug={slug}
						handleTitleChange={handleTitleChange}
						formikObj={formik}
					/>
					<p className="mb-0">{translate('Resume.basicDetailsText')}</p>
				</div>
				<div className="build-resume-edit-fields">
					<Row>
						<Col xl="6">
							<div className="text-start">
								<Form.Group className="mb-4 position-relative">
									<Form.Label className="lh-1">{translate('PlaceHolder.firstName')}</Form.Label>
									<Form.Control
										id="firstName"
										name="firstName"
										type="text"
										placeholder={translate('PlaceHolder.firstName')}
										onChange={(event) => handleChange(slug, event)}
										value={formik.values.firstName}
										onBlur={formik.handleBlur}
										ref={firstNameRef}
									/>
									{renderError('firstName')}
								</Form.Group>
							</div>
						</Col>
						<Col xl="6">
							<div className="text-start">
								<Form.Group className="mb-4 position-relative">
									<Form.Label className="lh-1">{translate('PlaceHolder.lastName')}</Form.Label>
									<Form.Control
										id="lastName"
										name="lastName"
										type="text"
										placeholder={translate('PlaceHolder.lastName')}
										onChange={(event) => handleChange(slug, event)}
										value={formik.values.lastName}
										onBlur={formik.handleBlur}
									/>
									{renderError('lastName')}
								</Form.Group>
							</div>
						</Col>
						<Col xl="6">
							<div className="text-start">
								<Form.Group className="mb-4 position-relative">
									<Form.Label className="lh-1">{translate('PlaceHolder.email')}</Form.Label>
									<Form.Control
										id="email"
										name="email"
										type="email"
										placeholder={translate('PlaceHolder.email')}
										onChange={(event) => handleChange(slug, event)}
										value={formik.values.email}
										onBlur={formik.handleBlur}
									/>
									{renderError('email')}
								</Form.Group>
							</div>
						</Col>
						<Col xl="6">
							<div className="text-start">
								<Form.Group className="mb-4 position-relative">
									<Form.Label className="lh-1">{translate('PlaceHolder.website')}</Form.Label>
									<Form.Control
										id="website"
										name="website"
										type="text"
										placeholder={translate('PlaceHolder.website')}
										onChange={(event) => handleChange(slug, event)}
										value={formik.values.website}
										onBlur={formik.handleBlur}
									/>
									{renderError('website')}
								</Form.Group>
							</div>
						</Col>
						<Col xl="6">
							<div className="text-start">
								<Form.Group className="mb-4 position-relative">
									<Form.Label className="lh-1">{translate('PlaceHolder.phone')}</Form.Label>
									<div className="number-input d-flex">
										<ReactSelect
											className="form-react-select"
											classNamePrefix="select"
											isSearchable
											name="countryCode"
											autoFocus={false}
											options={countries}
											getOptionLabel={(option: any) => option?.phonecode}
											getOptionValue={(option: any) => option?.phonecode}
											onChange={(value: any) => {
												formik.setFieldValue('countryCode', value?.phonecode);
												const eventObj = {
													target: {
														name: 'countryCode',
														value: value?.phonecode,
														innerText: value?.title,
														type: 'dropdown',
													},
												};
												handleChange(slug, eventObj);
											}}
											value={countries?.find(
												(item: any) => item.phonecode === formik.values.countryCode,
											)}
											onBlur={formik.handleBlur}
										/>
										<Form.Control
											type="text"
											id="phoneNumber"
											name="phoneNumber"
											className="rounded-pill flex-1"
											placeholder={translate('PlaceHolder.phone')}
											onChange={(event) => handleChange(slug, event)}
											onBlur={formik.handleBlur}
											value={formik.values.phoneNumber}
											autoComplete="off"
											maxLength={12}
										/>
									</div>
									{renderError('countryCode') || renderError('phoneNumber')}
								</Form.Group>
							</div>
						</Col>
						<Col xl="6">
							<div className="text-start">
								<Form.Group className="mb-4 position-relative">
									<Form.Label className="lh-1">{translate('PlaceHolder.jobTitle')}</Form.Label>
									<Form.Control
										id="designation"
										name="designation"
										type="text"
										placeholder={translate('PlaceHolder.jobTitle')}
										onChange={(event) => handleChange(slug, event)}
										value={formik.values.designation}
										onBlur={formik.handleBlur}
									/>
									{renderError('designation')}
								</Form.Group>
							</div>
						</Col>
						<Col xl="6">
							<div className="text-start">
								<Form.Group className="mb-4 position-relative">
									<Form.Label className="lh-1">{translate('Modal.industry')}</Form.Label>
									<ReactSelect
										className="basic-single"
										classNamePrefix="select"
										isSearchable
										name="industryId"
										options={industriesArr}
										getOptionValue={(option: any) => option.id}
										getOptionLabel={(option: any) => option.title}
										onBlur={formik.handleBlur}
										onChange={(value: any) => {
											const eventObj = {
												target: {
													name: 'industryId',
													value: value?.id,
													innerText: value?.title,
													type: 'dropdown',
												},
											};
											handleChange(slug, eventObj);
										}}
										value={industriesArr?.find(
											(value: any) => value.id === formik.values.industryId,
										)}
										autoFocus={false}
										placeholder={translate('PlaceHolder.selectIndustry')}
									/>
									{renderError('industryId')}
								</Form.Group>
							</div>
						</Col>
						<Col xl="6">
							<div className="text-start">
								<Form.Group className="mb-4 position-relative custom-search-address">
									<Form.Label className="lh-1">{translate('PlaceHolder.location')}</Form.Label>
									<CustomSearchAddress
										selectProps={{
											placeholder: translate('PlaceHolder.locationText'),
											onChange: (value: any) => {
												const eventObj = {
													target: {
														name: 'address',
														value: value,
													},
												};
												formik.setFieldValue('address', value?.location);
												formik.setFieldValue('cityId', value?.cityId);
												formik.setFieldValue('stateId', value?.stateId);
												formik.setFieldValue('countryId', value?.countryId);
												handleChange(slug, eventObj);
											},
											name: 'address',
										}}
										fieldValue={formik.values.address}
										handleInputChange={handleInputChange}
										onBlur={formik.handleBlur}
										formik={formik}
									/>
									<i className="ri-search-line form-icon position-absolute lh-1"></i>
									{renderError('address') ||
										renderError('cityId') ||
										renderError('stateId') ||
										renderError('countryId')}
								</Form.Group>
							</div>
						</Col>
					</Row>
				</div>
			</Form>
		</div>
	);
};

export default PersonalInfo;
